import React from "react";
import type { RdHoursWeekdayName } from "../../../../../shared/api-types";
import {
  dayOfWeekLabel,
  outsideVisits,
  weekRangeLabel,
  type RdHoursErrorKind,
  type WeekVisits,
} from "../../../lib/rd-hours-client";
import {
  isDraftDirty,
  type BlockRef,
  type DraftAction,
  type DraftState,
  type RdHoursMode,
} from "../../../lib/rd-hours-draft";
import {
  breakBlockedMessage,
  breakViolations,
  weekBookableMinutes,
} from "../../../lib/rd-hours-editor";
import { RdHoursGrid, focusAfterDelete, focusGrid } from "./grid";
import { isEditingLocked } from "./read-failure";
import { LiveRegion } from "./live-region";
import { SaveBar } from "./save-bar";
import { SidePanelCard } from "./side-panel";
import { SummaryCard } from "./summary-card";
import { RdHoursToolbar } from "./toolbar";

// The Availability editor's drawing (NO-878): every control, from a draft and
// callbacks, with no data fetching, so a static render shows any state.

export interface EditorViewProps {
  draft: DraftState;
  dispatch: (action: DraftAction) => void;
  /** One week's Monday; null in Every week. */
  weekStart: string | null;
  nextWeekStart: string;
  thresholdMinutes: number;
  /** The RD has never saved hours. */
  fresh: boolean;
  /** The week's visits; null when the viewer's reach hides them. */
  bookedVisits: WeekVisits | null;
  showVisits: boolean;
  onShowVisits: (checked: boolean) => void;
  canPreviousWeek: boolean;
  onMode: (mode: RdHoursMode) => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  /** A save is in flight. */
  saving: boolean;
  /** A read-back is under way: locked like a save, but Save keeps its label. */
  resolving: boolean;
  error: RdHoursErrorKind | null;
  saved: boolean;
  onSave: () => void;
  onLoadLatest: () => void;
}

const HINTS = {
  template:
    "Your usual week. Click an empty hour to add it, or select hours to change them and their lunch break.",
} as const;

function gridCopy(
  mode: RdHoursMode,
  weekStart: string | null
): { label: string; hint: string } {
  if (mode === "week" && weekStart !== null) {
    const range = weekRangeLabel(weekStart, false);

    return {
      label: `Hours for ${range}`,
      hint: `Changes here apply only to ${range}. Your usual week stays the same.`,
    };
  }

  return { label: "Weekly hours", hint: HINTS.template };
}

function visibleOutside(props: EditorViewProps, week: string | null) {
  if (week === null || props.bookedVisits === null) return { count: 0, lines: [] };

  return outsideVisits({
    visits: props.bookedVisits,
    week: props.draft.days,
    edited: props.draft.edited,
    weekStart: week,
  });
}

/** The changed days named for the summary; none while no week is loaded. */
function changedDayLabels(
  week: string | null,
  changed: readonly RdHoursWeekdayName[]
): string[] {
  return week === null ? [] : changed.map((day) => dayOfWeekLabel(week, day));
}

function EditorAside(
  props: EditorViewProps & {
    week: string | null;
    deleteBlock: (block: BlockRef) => void;
  }
) {
  const { draft, dispatch, week } = props;
  const selection = draft.selection;
  const outside = visibleOutside(props, week);

  return (
    <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-3.5">
      <SidePanelCard
        mode={draft.mode}
        weekStart={week}
        nextWeekStart={props.nextWeekStart}
        days={draft.days}
        selection={selection}
        thresholdMinutes={props.thresholdMinutes}
        copyTo={draft.copyTo}
        fresh={props.fresh}
        onRange={(start, end) =>
          selection !== null &&
          dispatch({ type: "set_range", block: selection, start, end })
        }
        onAddBreak={() => dispatch({ type: "add_break" })}
        onMoveBreak={(delta) => dispatch({ type: "move_break", delta })}
        onBreakLength={(minutes) => dispatch({ type: "break_length", minutes })}
        onRemoveBreak={() => dispatch({ type: "remove_break" })}
        onToggleCopy={(day) => dispatch({ type: "toggle_copy", day })}
        onApplyCopy={() => dispatch({ type: "apply_copy" })}
        onDelete={() => selection !== null && props.deleteBlock(selection)}
      />
      <SummaryCard
        mode={draft.mode}
        weekStart={week}
        nextWeekStart={props.nextWeekStart}
        bookableMinutes={weekBookableMinutes(draft.days)}
        fresh={props.fresh}
        changedLabels={changedDayLabels(week, draft.changed)}
        showVisits={props.bookedVisits === null ? null : props.showVisits}
        onShowVisits={props.onShowVisits}
        outside={outside}
      />
      <SaveBar
        mode={draft.mode}
        blockedDays={breakViolations(draft.days, props.thresholdMinutes)}
        blockedMessage={breakBlockedMessage(draft.days, props.thresholdMinutes)}
        dirty={isDraftDirty(draft)}
        saving={props.saving}
        resolving={props.resolving}
        error={props.error}
        saved={props.saved}
        onSave={props.onSave}
        onDiscard={() => dispatch({ type: "discard" })}
        onLoadLatest={props.onLoadLatest}
      />
    </aside>
  );
}

/** While a save is in flight nothing may be edited: the answer rebuilds the
 * draft, so an edit made in between would be lost. */
export function editDispatch(
  locked: boolean,
  dispatch: (action: DraftAction) => void
): (action: DraftAction) => void {
  return locked ? () => {} : dispatch;
}

/** Focus is lost when the element that held it unmounted (it falls to body). */
function focusLost(): boolean {
  const active = document.activeElement;

  return active === null || active === document.body;
}

/**
 * Where focus goes when the editor's own controls drop it: after a save the
 * editor remounts and Save is gone, so focus returns to the grid; after a
 * delete it moves to the next block or the cell where the block began.
 */
function useEditorFocus(saved: boolean) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [deleted, setDeleted] = React.useState<{
    block: BlockRef;
    start: number;
  } | null>(null);

  React.useEffect(() => {
    if (saved && focusLost()) focusGrid(rootRef.current);
  }, []); // Mount only: a save remounts the editor with `saved` set.

  React.useEffect(() => {
    if (deleted === null) return;

    focusAfterDelete(rootRef.current, deleted.block, deleted.start);
    setDeleted(null);
  }, [deleted]);

  return { rootRef, onDeleted: setDeleted };
}

export function RdHoursEditorView(viewProps: EditorViewProps) {
  const dispatch = editDispatch(
    isEditingLocked(viewProps.saving, viewProps.resolving),
    viewProps.dispatch
  );
  const focus = useEditorFocus(viewProps.saved);

  const deleteBlock = (block: BlockRef) => {
    const current = viewProps.draft.days[block.day][block.index];

    dispatch({ type: "delete", block });
    if (current !== undefined) focus.onDeleted({ block, start: current.start });
  };

  const props = { ...viewProps, dispatch, deleteBlock };
  const { draft, weekStart } = props;
  const week = draft.mode === "week" && weekStart !== null ? weekStart : null;
  const copy = gridCopy(draft.mode, week);
  const selection = draft.selection;
  const visits =
    props.bookedVisits !== null && props.showVisits ? props.bookedVisits : null;

  return (
    <div ref={focus.rootRef}>
      <LiveRegion message={draft.announcement} />
      <RdHoursToolbar
        mode={draft.mode}
        onMode={props.onMode}
        weekLabel={week === null ? null : weekRangeLabel(week, true)}
        canPrevious={props.canPreviousWeek}
        onPreviousWeek={props.onPreviousWeek}
        onNextWeek={props.onNextWeek}
        timeZone={draft.timeZone}
        onTimeZone={(timeZone) => dispatch({ type: "time_zone", timeZone })}
        zoneDisabled={draft.mode === "week"}
      />
      <div className="flex flex-wrap items-start gap-5">
        <RdHoursGrid
          label={copy.label}
          hint={copy.hint}
          mode={draft.mode}
          weekStart={week}
          days={draft.days}
          changed={draft.changed}
          selection={selection}
          thresholdMinutes={props.thresholdMinutes}
          visits={visits}
          edited={draft.edited}
          onAddHour={(day, hourStart) => dispatch({ type: "add_hour", day, hourStart })}
          onCreate={(day, from, to) => dispatch({ type: "create", day, from, to })}
          onResize={(block, start, end) =>
            dispatch({ type: "set_range", block, start, end })
          }
          onSelect={(block) => dispatch({ type: "select", block })}
          onDelete={props.deleteBlock}
        />
        <EditorAside {...props} week={week} />
      </div>
    </div>
  );
}
